import React from 'react';
import {
  Box,
  Flex,
  Text,
  Heading,
  Grid,
  GridItem,
  VStack,
  HStack,
  useColorModeValue,
  Icon,
  Badge,
  Progress,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td
} from '@chakra-ui/react';
import { CheckCircle, XCircle, Clock, AlertTriangle, FileText, Shield } from 'lucide-react';

const CompliancePanel: React.FC = () => {
  // Colors
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'gray.300');
  
  // Compliance metrics
  const complianceMetrics = [
    { name: 'HIPAA', score: 98, status: 'Compliant', lastAudit: '2025-02-15' },
    { name: 'GDPR', score: 92, status: 'Compliant', lastAudit: '2025-02-10' },
    { name: 'CCPA', score: 95, status: 'Compliant', lastAudit: '2025-01-28' },
    { name: 'NIST', score: 89, status: 'Compliant', lastAudit: '2025-01-20' }
  ];
  
  // Recent compliance activities
  const recentActivities = [
    { id: 1, type: 'Audit', description: 'Quarterly HIPAA compliance audit completed', date: '2025-03-01', status: 'Completed' },
    { id: 2, type: 'Review', description: 'Data retention policy review', date: '2025-02-25', status: 'Completed' },
    { id: 3, type: 'Update', description: 'Privacy policy updated for new features', date: '2025-02-20', status: 'Completed' },
    { id: 4, type: 'Training', description: 'Staff compliance training', date: '2025-02-15', status: 'Completed' },
    { id: 5, type: 'Audit', description: 'Annual security assessment', date: '2025-03-10', status: 'Scheduled' }
  ];
  
  return (
    <Grid templateColumns="repeat(12, 1fr)" gap={4}>
      {/* Left column */}
      <GridItem colSpan={{ base: 12, md: 8 }}>
        <VStack spacing={4} align="stretch">
          {/* Compliance overview */}
          <Box bg={bgColor} p={4} borderRadius="lg" boxShadow="sm" borderWidth="1px" borderColor={borderColor}>
            <Heading size="sm" mb={3}>Compliance Overview</Heading>
            <Grid templateColumns="repeat(4, 1fr)" gap={4}>
              {complianceMetrics.map((metric, index) => (
                <Box key={index} p={3} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                  <VStack spacing={1} align="start">
                    <HStack>
                      <Text fontWeight="bold">{metric.name}</Text>
                      <Badge colorScheme={metric.score >= 90 ? 'green' : 'yellow'}>{metric.status}</Badge>
                    </HStack>
                    <Progress
                      value={metric.score}
                      size="sm"
                      width="100%"
                      colorScheme={metric.score >= 90 ? 'green' : metric.score >= 80 ? 'yellow' : 'red'}
                      borderRadius="full"
                      mt={1}
                    />
                    <Text fontSize="xs" color="gray.500">Last audit: {metric.lastAudit}</Text>
                  </VStack>
                </Box>
              ))}
            </Grid>
          </Box>
          
          {/* Recent compliance activities */}
          <Box bg={bgColor} p={4} borderRadius="lg" boxShadow="sm" borderWidth="1px" borderColor={borderColor}>
            <Heading size="sm" mb={3}>Recent Compliance Activities</Heading>
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>Type</Th>
                  <Th>Description</Th>
                  <Th>Date</Th>
                  <Th>Status</Th>
                </Tr>
              </Thead>
              <Tbody>
                {recentActivities.map((activity) => (
                  <Tr key={activity.id}>
                    <Td>
                      <HStack>
                        <Icon 
                          as={activity.type === 'Audit' ? Shield : FileText} 
                          color={activity.type === 'Audit' ? 'blue.500' : 'green.500'} 
                          boxSize={4} 
                        />
                        <Text>{activity.type}</Text>
                      </HStack>
                    </Td>
                    <Td>{activity.description}</Td>
                    <Td>{activity.date}</Td>
                    <Td>
                      <Badge
                        colorScheme={activity.status === 'Completed' ? 'green' : 'blue'}
                        variant="subtle"
                      >
                        {activity.status}
                      </Badge>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </VStack>
      </GridItem>
      
      {/* Right column */}
      <GridItem colSpan={{ base: 12, md: 4 }}>
        <VStack spacing={4} align="stretch">
          {/* Overall compliance score */}
          <Box bg={bgColor} p={4} borderRadius="lg" boxShadow="sm" borderWidth="1px" borderColor={borderColor}>
            <Stat>
              <StatLabel>Overall Compliance Score</StatLabel>
              <StatNumber>94%</StatNumber>
              <StatHelpText>
                <StatArrow type="increase" />
                3% since last quarter
              </StatHelpText>
              <Progress value={94} size="sm" colorScheme="green" mt={2} />
            </Stat>
          </Box>
          
          {/* Compliance calendar */}
          <Box bg={bgColor} p={4} borderRadius="lg" boxShadow="sm" borderWidth="1px" borderColor={borderColor}>
            <Heading size="sm" mb={3}>Upcoming Compliance Events</Heading>
            <VStack spacing={3} align="stretch">
              <HStack p={2} bg="blue.50" borderRadius="md">
                <Icon as={Clock} color="blue.500" />
                <VStack spacing={0} align="start">
                  <Text fontWeight="bold" fontSize="sm">Annual Security Assessment</Text>
                  <Text fontSize="xs">March 10, 2025</Text>
                </VStack>
              </HStack>
              
              <HStack p={2} bg="purple.50" borderRadius="md">
                <Icon as={FileText} color="purple.500" />
                <VStack spacing={0} align="start">
                  <Text fontWeight="bold" fontSize="sm">Policy Review Meeting</Text>
                  <Text fontSize="xs">March 15, 2025</Text>
                </VStack>
              </HStack>
              
              <HStack p={2} bg="green.50" borderRadius="md">
                <Icon as={Shield} color="green.500" />
                <VStack spacing={0} align="start">
                  <Text fontWeight="bold" fontSize="sm">Staff Training Session</Text>
                  <Text fontSize="xs">March 22, 2025</Text>
                </VStack>
              </HStack>
            </VStack>
          </Box>
        </VStack>
      </GridItem>
    </Grid>
  );
};

export default CompliancePanel;
